import {
	Box,
	Button,
	Container,
	LoadingOverlay,
	NumberInput,
	Text,
	Group,
} from "@mantine/core";
import {
	IconPlus,
	IconTrash,
	IconX,
	IconPlayerPause,
	IconPlayerPlay,
	IconRefresh,
	IconTag,
} from "@tabler/icons-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocalStorage } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import EmptyState from "../components/common/EmptyState";
import LoadingSpinner from "../components/common/LoadingSpinner";
import PageHeader from "../components/common/PageHeader";
import Pagination from "../components/common/Pagination";
import BulkRenewModal from "../components/licenses/BulkRenewModal";
import BulkSetTypeModal from "../components/licenses/BulkSetTypeModal";
import LicenseFilters from "../components/licenses/LicenseFilters";
import LicenseModal, {
	type LicenseFormData,
} from "../components/licenses/LicenseModal";
import LicenseOverview from "../components/licenses/LicenseOverview";
import LicenseTable, {
	type LicenseSortField,
	type SortDirection,
} from "../components/licenses/LicenseTable";
import { licensesAPI } from "../services/api";
import { fetchAccountOptions } from "../store/accounts/accountsSlice";
import { fetchLicenseTypes } from "../store/licenseTypes/licenseTypesSlice";
import {
	clearError,
	createLicense,
	deleteLicense,
	fetchLicenses,
	fetchLicenseStats,
	fetchFilteredLicenseStats,
	clearFilteredStats,
	holdLicense,
	updateLicense,
	bulkRevokeLicenses,
	bulkHoldLicenses,
	bulkReleaseLicenses,
	bulkRenewLicenses,
	bulkDeleteLicenses,
	bulkSetLicenseType,
	type License,
} from "../store/licenses/licensesSlice";
import type { AppDispatch, RootState } from "../store/store";
import { toExpirationTimestamp, toSubmittedExpiration } from "../utils/dates";

// Matches licenses with no licenseTypeId - the shape that predates license
// types, and bills at zero until the backfill has run.
const UNASSIGNED_TYPE_FILTER = "none";

function LicensesPage() {
	const dispatch = useDispatch<AppDispatch>();
	const { licenses, loading, error, listError, total, stats, filteredStats } =
		useSelector((state: RootState) => state.licenses);
	const { options: accounts, hasLoadedOptions } = useSelector(
		(state: RootState) => state.accounts,
	);
	const { licenseTypes } = useSelector(
		(state: RootState) => state.licenseTypes
	);
	const { user: currentUser } = useSelector((state: RootState) => state.auth);

	const [modalOpened, setModalOpened] = useState(false);
	const [editingLicense, setEditingLicense] = useState<License | null>(null);
	const [bulkRenewOpened, setBulkRenewOpened] = useState(false);
	const [bulkSetTypeOpened, setBulkSetTypeOpened] = useState(false);
	const [isSelectingAll, setIsSelectingAll] = useState(false);
	// Bumped whenever the selection is reset, so an id fetch that lands after
	// the filters changed does not select licenses that no longer match
	const selectionScopeRef = useRef(0);
	// Separate from the slice's loading flag, which every list fetch also
	// flips: a fetch finishing mid-save re-enabled Create, and a second click
	// made a duplicate license.
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [searchTerm, setSearchTerm] = useLocalStorage({
		key: "licenses-search-term",
		defaultValue: "",
		getInitialValueInEffect: false,
	});
	const [statusFilter, setStatusFilter] = useLocalStorage({
		key: "licenses-status-filter",
		defaultValue: "",
		getInitialValueInEffect: false,
	});
	const [accountFilter, setAccountFilter] = useLocalStorage({
		key: "licenses-account-filter",
		defaultValue: "",
		getInitialValueInEffect: false,
	});
	const [licenseTypeFilter, setLicenseTypeFilter] = useLocalStorage({
		key: "licenses-license-type-filter",
		defaultValue: "",
		getInitialValueInEffect: false,
	});
	const [currentPage, setCurrentPage] = useState(1);
	const [refreshKey, setRefreshKey] = useState(0);
	const [selectedLicenses, setSelectedLicenses] = useState<string[]>([]);
	const [sortField, setSortField] = useState<LicenseSortField>(null);
	const [sortDirection, setSortDirection] = useState<SortDirection>(null);

	// Stable identity: LicenseFilters re-runs its debounce effect whenever this changes
	const handleSearchChange = useCallback(
		(value: string) => {
			setSearchTerm(value);
			setCurrentPage(1);
		},
		[setSearchTerm],
	);

	// Sorting is applied by the API over the whole result set. Reordering the
	// rows here would only ever reach the ten already on screen.
	const handleSort = (field: LicenseSortField) => {
		if (sortField === field) {
			if (sortDirection === "desc") {
				setSortDirection("asc");
			} else if (sortDirection === "asc") {
				setSortField(null);
				setSortDirection(null);
			} else {
				setSortDirection("desc");
			}
		} else {
			setSortField(field);
			setSortDirection("desc");
		}
		setCurrentPage(1);
	};

	const totalPages = Math.ceil(total / 10);

	// Refetching through the effect below rather than dispatching here keeps one
	// source of the page number. A handler that dispatched its own fetch used the
	// page captured when it ran, which is the wrong one once a delete shrinks the
	// result set and the page number has to come back.
	const refreshData = () => {
		setRefreshKey((key) => key + 1);
		dispatch(fetchLicenseStats());
	};

	useEffect(() => {
		// Its error lands in the accounts slice, which only the Accounts page
		// reports - here an empty account dropdown was the only symptom
		dispatch(fetchAccountOptions())
			.unwrap()
			.catch((message: string) => {
				notifications.show({ title: "Couldn't load accounts", message, color: "red" });
			});
		// Explicit high limit: the endpoint pages at 25 by default, which would
		// silently drop types from the filter and the create form.
		dispatch(fetchLicenseTypes({ page: 1, limit: 200 }));
		dispatch(fetchLicenseStats());
	}, [dispatch]);

	useEffect(() => {
		const params = {
			page: currentPage,
			limit: 10,
			search: searchTerm || undefined,
			accountId: accountFilter || undefined,
			licenseTypeId: licenseTypeFilter || undefined,
			status: statusFilter || undefined,
			sortBy: sortField ?? undefined,
			sortOrder: sortDirection ?? undefined,
		};

		dispatch(fetchLicenses(params));
	}, [
		dispatch,
		currentPage,
		searchTerm,
		accountFilter,
		licenseTypeFilter,
		statusFilter,
		sortField,
		sortDirection,
		refreshKey,
	]);

	// Filtered counts sit under each Overview total, over exactly what the
	// table shows. refreshKey: an edit can move licenses in or out of the filter.
	const hasFilters = Boolean(searchTerm || statusFilter || accountFilter || licenseTypeFilter);
	useEffect(() => {
		if (!hasFilters) {
			dispatch(clearFilteredStats());
			return;
		}
		dispatch(
			fetchFilteredLicenseStats({
				search: searchTerm || undefined,
				status: statusFilter || undefined,
				accountId: accountFilter || undefined,
				licenseTypeId: licenseTypeFilter || undefined,
			}),
		);
	}, [dispatch, hasFilters, searchTerm, statusFilter, accountFilter, licenseTypeFilter, refreshKey]);

	const filterLabel = [
		accounts.find((account) => account._id === accountFilter)?.name,
		licenseTypeFilter === UNASSIGNED_TYPE_FILTER
			? "Unassigned"
			: licenseTypes.find((licenseType) => licenseType._id === licenseTypeFilter)?.name,
		statusFilter && statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1),
		searchTerm && `"${searchTerm}"`,
	]
		.filter(Boolean)
		.join(" · ");

	// Selection is per page: a bulk action must not reach rows the user can no
	// longer see. Selecting every matching license is an explicit opt-out.
	useEffect(() => {
		selectionScopeRef.current += 1;
		setSelectedLicenses([]);
	}, [
		currentPage,
		searchTerm,
		accountFilter,
		licenseTypeFilter,
		statusFilter,
		sortField,
		sortDirection,
	]);

	// A result set that shrinks under the current page - a delete, a bulk action,
	// licenses revoked elsewhere - leaves this page past the end. That renders as
	// "no licenses match", with no pagination left to navigate back with.
	useEffect(() => {
		if (totalPages > 0 && currentPage > totalPages) {
			setCurrentPage(totalPages);
		}
	}, [currentPage, totalPages]);

	// A persisted account filter can outlive the account it points at; drop it
	// once the full account list has loaded without it, so the list isn't
	// silently empty. Only once that list is in: checked against whatever was
	// in the store before, e.g. one searched page of Accounts, it cleared
	// filters for accounts that still exist.
	useEffect(() => {
		if (
			hasLoadedOptions &&
			accountFilter &&
			!accounts.some((account) => account._id === accountFilter)
		) {
			setAccountFilter("");
		}
	}, [accountFilter, accounts, hasLoadedOptions, setAccountFilter]);

	// Same for a persisted type filter whose type has since been deleted. The
	// "unassigned" sentinel is not a type id, so it is never stale.
	useEffect(() => {
		if (
			licenseTypeFilter &&
			licenseTypeFilter !== UNASSIGNED_TYPE_FILTER &&
			licenseTypes.length > 0 &&
			!licenseTypes.some((licenseType) => licenseType._id === licenseTypeFilter)
		) {
			setLicenseTypeFilter("");
		}
	}, [licenseTypeFilter, licenseTypes, setLicenseTypeFilter]);

	useEffect(() => {
		if (error) {
			notifications.show({
				title: "Error",
				message: error,
				color: "red",
			});
			dispatch(clearError());
		}
	}, [error, dispatch]);

	const handleCreateLicense = async (values: LicenseFormData) => {
		setIsSubmitting(true);
		try {
			await dispatch(
				createLicense({
					name: values.name,
					accountId: values.accountId,
					os: values.os,
					licenseTypeId: values.licenseTypeId,
					isUnlimited: values.isUnlimited,
					expirationDate: values.isUnlimited
						? undefined
						: toExpirationTimestamp(values.expirationDate),
					tags: values.tags,
				}),
			).unwrap();
			setModalOpened(false);
			refreshData();
			notifications.show({
				title: "Success",
				message: "License created successfully",
				color: "green",
			});
		} catch {
			// Error handled by Redux
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleUpdateLicense = async (values: LicenseFormData) => {
		if (!editingLicense) return;

		setIsSubmitting(true);
		try {
			await dispatch(
				updateLicense({
					id: editingLicense._id,
					name: values.name,
					accountId: values.accountId,
					isActive: editingLicense.isActive,
					licenseTypeId: values.licenseTypeId,
					isUnlimited: values.isUnlimited,
					expirationDate: values.isUnlimited
						? undefined
						: toSubmittedExpiration(values.expirationDate, editingLicense.expirationDate),
					tags: values.tags,
				}),
			).unwrap();
			setModalOpened(false);
			setEditingLicense(null);
			refreshData();
			notifications.show({
				title: "Success",
				message: "License updated successfully",
				color: "green",
			});
		} catch {
			// Error handled by Redux
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleToggleLicense = async (license: License) => {
		try {
			await dispatch(
				updateLicense({
					id: license._id,
					name: license.name,
					isActive: !license.isActive,
					isUnlimited: license.isUnlimited,
					expirationDate: license.isUnlimited
						? undefined
						: license.expirationDate,
					tags: license.tags,
				}),
			).unwrap();
			refreshData();
			notifications.show({
				title: "Success",
				message: `License ${
					license.isActive ? "deactivated" : "activated"
				} successfully`,
				color: "green",
			});
		} catch {
			// Error handled by Redux
		}
	};

	const handleDeleteLicense = (id: string, name: string) => {
		modals.openConfirmModal({
			title: "Delete License",
			children: (
				<Text>
					Are you sure you want to delete the license "{name}"? This action
					cannot be undone.
				</Text>
			),
			labels: { confirm: "Delete", cancel: "Cancel" },
			confirmProps: { color: "red" },
			onConfirm: async () => {
				try {
					await dispatch(deleteLicense(id)).unwrap();
					refreshData();
					notifications.show({
						title: "Success",
						message: "License deleted successfully",
						color: "green",
					});
				} catch {
					// Error handled by Redux
				}
			},
		});
	};

	const openModal = (license?: License) => {
		setEditingLicense(license || null);
		setModalOpened(true);
	};

	const handleRevokeLicense = (license: License) => {
		modals.openConfirmModal({
			title: "Revoke License",
			children: (
				<Text>
					Are you sure you want to revoke the license "{license.name}"? This
					will immediately deactivate the license and cannot be undone.
				</Text>
			),
			labels: { confirm: "Revoke", cancel: "Cancel" },
			confirmProps: { color: "red" },
			onConfirm: async () => {
				try {
					await dispatch(
						updateLicense({
							id: license._id,
							name: license.name,
							isActive: false,
							isUnlimited: license.isUnlimited,
							expirationDate: license.isUnlimited
								? undefined
								: license.expirationDate,
							tags: license.tags,
							status: "revoked",
						}),
					).unwrap();
					refreshData();
					notifications.show({
						title: "Success",
						message: "License revoked successfully",
						color: "green",
					});
				} catch {
					// Error handled by Redux
				}
			},
		});
	};

	const handleReuseLicense = (license: License) => {
		modals.openConfirmModal({
			title: "Re-use License",
			children: (
				<Text>
					Reset the revoked license "{license.name}" so it can be activated
					again? It returns to the initialized state and any station can claim
					it with a new client UID.
				</Text>
			),
			labels: { confirm: "Re-use", cancel: "Cancel" },
			onConfirm: async () => {
				try {
					await dispatch(
						updateLicense({
							id: license._id,
							name: license.name,
							isActive: false,
							isUnlimited: license.isUnlimited,
							expirationDate: license.isUnlimited
								? undefined
								: license.expirationDate,
							tags: license.tags,
							status: "initialized",
						}),
					).unwrap();
					refreshData();
					notifications.show({
						title: "Success",
						message: "License is ready to be activated again",
						color: "green",
					});
				} catch {
					// Error handled by Redux
				}
			},
		});
	};

	const handleRenewLicense = (license: License) => {
		let extensionMonths = 12; // Default to 1 year

		const modalId = modals.open({
			title: "Renew License",
			children: (
				<div>
					<Text mb="md">
						How many months would you like to extend the license "{license.name}
						"?
					</Text>
					<NumberInput
						label="Extension (months)"
						defaultValue={12}
						min={1}
						max={60}
						onChange={(value) => {
							extensionMonths = typeof value === "number" ? value : 12;
						}}
					/>
					<Text
						size="sm"
						c="dimmed"
						mt="sm">
						Current expiration:{" "}
						{license.expirationDate
							? new Date(license.expirationDate).toLocaleDateString("he-IL")
							: "-"}
					</Text>
					<div
						style={{
							display: "flex",
							justifyContent: "flex-end",
							gap: "12px",
							marginTop: "16px",
						}}>
						<Button
							variant="outline"
							onClick={() => modals.close(modalId)}>
							Cancel
						</Button>
						<Button
							onClick={async () => {
								try {
									// Renew is only offered once a license has expired, so
									// counting the extension from the old date can land it in
									// the past again - which the API rejects outright.
									const now = new Date();
									const currentExpiration = license.expirationDate
										? new Date(license.expirationDate)
										: now;
									const newExpiration =
										currentExpiration > now ? new Date(currentExpiration) : now;
									newExpiration.setMonth(
										newExpiration.getMonth() + extensionMonths,
									);

									await dispatch(
										updateLicense({
											id: license._id,
											name: license.name,
											isActive: license.isActive,
											expirationDate: newExpiration.toISOString(),
											tags: license.tags,
										}),
									).unwrap();
									refreshData();
									notifications.show({
										title: "Success",
										message: `License renewed for ${extensionMonths} months`,
										color: "green",
									});
									modals.close(modalId);
								} catch {
									// Error handled by Redux
								}
							}}>
							Renew
						</Button>
					</div>
				</div>
			),
		});
	};

	const handleHoldLicense = async (
		license: License,
		action: "hold" | "release",
	) => {
		try {
			await dispatch(holdLicense({ id: license._id, action })).unwrap();
			refreshData();
			notifications.show({
				title: "Success",
				message: `License ${
					action === "hold" ? "put on hold" : "released from hold"
				} successfully`,
				color: "green",
			});
		} catch {
			// Error handled by Redux
		}
	};

	const handleSelectAllMatching = async () => {
		const scope = selectionScopeRef.current;
		setIsSelectingAll(true);
		try {
			const response = await licensesAPI.getIds({
				search: searchTerm || undefined,
				accountId: accountFilter || undefined,
				licenseTypeId: licenseTypeFilter || undefined,
				status: statusFilter || undefined,
			});
			if (scope === selectionScopeRef.current) {
				setSelectedLicenses(response.data.data?.ids ?? []);
			}
		} catch (error) {
			const message = (error as { response?: { data?: { message?: string } } })
				?.response?.data?.message;
			notifications.show({
				title: "Error",
				message: message || "Failed to select all matching licenses",
				color: "red",
			});
		} finally {
			setIsSelectingAll(false);
		}
	};

	// Bulk action handlers
	const handleBulkRevoke = () => {
		modals.openConfirmModal({
			title: "Revoke Licenses",
			children: (
				<Text>
					Are you sure you want to revoke {selectedLicenses.length} selected
					license(s)? This will immediately deactivate them and cannot be
					undone.
				</Text>
			),
			labels: { confirm: "Revoke", cancel: "Cancel" },
			confirmProps: { color: "red" },
			onConfirm: async () => {
				try {
					const { count } = await dispatch(
						bulkRevokeLicenses(selectedLicenses),
					).unwrap();
					setSelectedLicenses([]);
					refreshData();
					notifications.show({
						title: "Success",
						message: `${count} license(s) revoked successfully`,
						color: "green",
					});
				} catch {
					// Error handled by Redux
				}
			},
		});
	};

	const handleBulkHold = () => {
		modals.openConfirmModal({
			title: "Hold Licenses",
			children: (
				<Text>
					Are you sure you want to put {selectedLicenses.length} selected
					license(s) on hold?
				</Text>
			),
			labels: { confirm: "Hold", cancel: "Cancel" },
			confirmProps: { color: "yellow" },
			onConfirm: async () => {
				try {
					const { count } = await dispatch(
						bulkHoldLicenses(selectedLicenses),
					).unwrap();
					setSelectedLicenses([]);
					refreshData();
					notifications.show({
						title: "Success",
						message: `${count} license(s) put on hold successfully`,
						color: "green",
					});
				} catch {
					// Error handled by Redux
				}
			},
		});
	};

	const handleBulkRelease = () => {
		modals.openConfirmModal({
			title: "Release Licenses",
			children: (
				<Text>
					Are you sure you want to release {selectedLicenses.length} selected
					license(s) from hold?
				</Text>
			),
			labels: { confirm: "Release", cancel: "Cancel" },
			confirmProps: { color: "blue" },
			onConfirm: async () => {
				try {
					const { count } = await dispatch(
						bulkReleaseLicenses(selectedLicenses),
					).unwrap();
					setSelectedLicenses([]);
					refreshData();
					notifications.show({
						title: "Success",
						message: `${count} license(s) released successfully`,
						color: "green",
					});
				} catch {
					// Error handled by Redux
				}
			},
		});
	};

	const handleBulkRenew = async (expirationDate: string) => {
		setIsSubmitting(true);
		try {
			const { renewed, skipped, failed } = await dispatch(
				bulkRenewLicenses({
					licenseIds: selectedLicenses,
					expirationDate: toExpirationTimestamp(expirationDate),
				}),
			).unwrap();
			setBulkRenewOpened(false);
			setSelectedLicenses([]);
			refreshData();
			const details = [
				skipped > 0 && `${skipped} skipped (revoked or unlimited)`,
				failed > 0 && `${failed} could not be saved`,
			].filter(Boolean);
			notifications.show({
				title: failed > 0 ? "Partially renewed" : "Success",
				message:
					details.length > 0
						? `${renewed} license(s) renewed, ${details.join(", ")}`
						: `${renewed} license(s) renewed successfully`,
				color: failed > 0 ? "yellow" : "green",
			});
		} catch {
			// Error handled by Redux
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleBulkSetType = async (licenseTypeId: string) => {
		setIsSubmitting(true);
		try {
			const { count } = await dispatch(
				bulkSetLicenseType({ licenseIds: selectedLicenses, licenseTypeId }),
			).unwrap();
			setBulkSetTypeOpened(false);
			setSelectedLicenses([]);
			refreshData();
			notifications.show({
				title: "Success",
				message: `${count} license(s) updated`,
				color: "green",
			});
		} catch {
			// Error handled by Redux
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleBulkDelete = () => {
		modals.openConfirmModal({
			title: "Delete Licenses",
			children: (
				<Text>
					Are you sure you want to delete {selectedLicenses.length} selected
					license(s)? This action cannot be undone.
				</Text>
			),
			labels: { confirm: "Delete", cancel: "Cancel" },
			confirmProps: { color: "red" },
			onConfirm: async () => {
				try {
					await dispatch(bulkDeleteLicenses(selectedLicenses)).unwrap();
					setSelectedLicenses([]);
					refreshData();
					notifications.show({
						title: "Success",
						message: `${selectedLicenses.length} license(s) deleted successfully`,
						color: "green",
					});
				} catch {
					// Error handled by Redux
				}
			},
		});
	};

	// Overview stats from backend (covers all licenses, not just current page)
	const activeLicenses = stats.active;
	const inactiveLicenses = stats.inactive;
	const expiredLicenses = stats.expired;
	const heldLicenses = stats.held;

	// Prepare account options for filters and form
	const accountOptions = accounts.map((account) => ({
		value: account._id,
		label: account.name,
	}));

	// Only types still on offer can be assigned to a license...
	const licenseTypeOptions = licenseTypes
		.filter((licenseType) => licenseType.isActive)
		.map((licenseType) => ({
			value: licenseType._id,
			label: `${licenseType.name} (₪${licenseType.monthlyPrice.toFixed(2)}/mo)`,
		}));

	// ...but a withdrawn type still has licenses on it that need finding, and so
	// do the licenses that predate license types entirely.
	const licenseTypeFilterOptions = [
		...licenseTypes.map((licenseType) => ({
			value: licenseType._id,
			label: licenseType.isActive
				? licenseType.name
				: `${licenseType.name} (withdrawn)`,
		})),
		{ value: UNASSIGNED_TYPE_FILTER, label: "Unassigned" },
	];

	const canCreateLicenses =
		currentUser?.role === "admin" || currentUser?.role === "manager";

	const pageActions = canCreateLicenses && (
		<Button
			leftSection={<IconPlus size={16} />}
			onClick={() => openModal()}>
			Create License
		</Button>
	);

	return (
		<Container size="xl">
			<PageHeader
				title="Licenses"
				description="Manage software licenses and activations"
				actions={pageActions}
			/>

			<LicenseOverview
				total={stats.total}
				active={activeLicenses}
				inactive={inactiveLicenses}
				expired={expiredLicenses}
				held={heldLicenses}
				filtered={hasFilters ? filteredStats : null}
				filterLabel={filterLabel}
			/>

			<LicenseFilters
				searchTerm={searchTerm}
				statusFilter={statusFilter}
				accountFilter={accountFilter}
				licenseTypeFilter={licenseTypeFilter}
				licenseTypeOptions={licenseTypeFilterOptions}
				onLicenseTypeFilterChange={(value) => {
					setLicenseTypeFilter(value || "");
					setCurrentPage(1);
				}}
				accountOptions={accountOptions}
				onSearchChange={handleSearchChange}
				onStatusFilterChange={(value) => {
					setStatusFilter(value || "");
					setCurrentPage(1);
				}}
				onAccountFilterChange={(value) => {
					setAccountFilter(value || "");
					setCurrentPage(1);
				}}
			/>

			{selectedLicenses.length > 0 && canCreateLicenses && (
				<Group
					gap="sm"
					mb="md">
					<Text
						size="sm"
						c="dimmed">
						{selectedLicenses.length} license(s) selected
					</Text>
					{selectedLicenses.length < total &&
						licenses.every((license) => selectedLicenses.includes(license._id)) && (
							<Button
								size="xs"
								variant="subtle"
								loading={isSelectingAll}
								onClick={handleSelectAllMatching}>
								Select all {total} matching
							</Button>
						)}
					<Button
						size="xs"
						variant="light"
						color="orange"
						leftSection={<IconX size={14} />}
						onClick={handleBulkRevoke}>
						Revoke Selected
					</Button>
					<Button
						size="xs"
						variant="light"
						color="yellow"
						leftSection={<IconPlayerPause size={14} />}
						onClick={handleBulkHold}>
						Hold Selected
					</Button>
					<Button
						size="xs"
						variant="light"
						color="blue"
						leftSection={<IconPlayerPlay size={14} />}
						onClick={handleBulkRelease}>
						Release Selected
					</Button>
					<Button
						size="xs"
						variant="light"
						color="green"
						leftSection={<IconRefresh size={14} />}
						onClick={() => setBulkRenewOpened(true)}>
						Renew Selected
					</Button>
					<Button
						size="xs"
						variant="light"
						color="grape"
						leftSection={<IconTag size={14} />}
						onClick={() => setBulkSetTypeOpened(true)}>
						Set Type
					</Button>
					<Button
						size="xs"
						variant="light"
						color="red"
						leftSection={<IconTrash size={14} />}
						onClick={handleBulkDelete}>
						Delete Selected
					</Button>
				</Group>
			)}

			{/* The table keeps its height while a page loads. Swapping it for a
			    spinner collapsed the page by the table's full height, so the
			    browser clamped the scroll position and the controls below - which
			    sit under a 600px scroll container, off the fold - ended up out of
			    view. The nav was mounted the whole time; the page moved. */}
			{loading && licenses.length === 0 ? (
				<LoadingSpinner />
			) : listError ? (
				<EmptyState
					title="Couldn't load licenses"
					message={`${listError}. The page controls below still work - try again, or go back to a page that loaded.`}
				/>
			) : licenses.length === 0 ? (
				<EmptyState
					title="No licenses found"
					message={
						searchTerm || statusFilter || accountFilter || licenseTypeFilter
							? "No licenses match your search criteria."
							: "No licenses have been created yet. Create your first license to get started."
					}
				/>
			) : (
				<Box pos="relative">
					<LoadingOverlay
						visible={loading}
						overlayProps={{ blur: 1 }}
					/>
					<LicenseTable
						licenses={licenses}
						currentUserRole={currentUser?.role}
						selectedLicenses={selectedLicenses}
						sortField={sortField}
						sortDirection={sortDirection}
						onSort={handleSort}
						onSelectionChange={setSelectedLicenses}
						onEditLicense={openModal}
						onToggleLicense={handleToggleLicense}
						onDeleteLicense={handleDeleteLicense}
						onRevokeLicense={handleRevokeLicense}
						onReuseLicense={handleReuseLicense}
						onRenewLicense={handleRenewLicense}
						onHoldLicense={handleHoldLicense}
					/>
				</Box>
			)}

			{/* Outside the loading branch: swapping the table for a spinner must not
			    take the page controls with it on every page change. */}
			<Pagination
				currentPage={currentPage}
				totalPages={totalPages}
				onPageChange={setCurrentPage}
			/>

			<LicenseModal
				opened={modalOpened}
				license={editingLicense}
				loading={isSubmitting}
				accounts={accountOptions}
				licenseTypes={licenseTypeOptions}
				onClose={() => setModalOpened(false)}
				onSubmit={editingLicense ? handleUpdateLicense : handleCreateLicense}
			/>

			<BulkRenewModal
				opened={bulkRenewOpened}
				count={selectedLicenses.length}
				loading={isSubmitting}
				onClose={() => setBulkRenewOpened(false)}
				onSubmit={handleBulkRenew}
			/>

			<BulkSetTypeModal
				opened={bulkSetTypeOpened}
				count={selectedLicenses.length}
				loading={isSubmitting}
				licenseTypes={licenseTypeOptions}
				onClose={() => setBulkSetTypeOpened(false)}
				onSubmit={handleBulkSetType}
			/>
		</Container>
	);
}

export default LicensesPage;
